// Eskom PARCC — authentication context.
//
// A thin layer over Firebase Auth (email/password). The admin provisions all
// users out-of-band, so there is no sign-up and no password-reset-email flow;
// v1 supports sign-in, sign-out and self-service password change only.
//
// Role gating drives which screens a user sees. Until the `User` Data Connect
// query lands, role is resolved by `resolveRole` via a fixed precedence (see
// below). It is the single swap-point for the future SQL lookup.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  reauthenticateWithPopup,
  updatePassword,
  linkWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  type User,
} from 'firebase/auth';
import { auth } from './firebase';
import { normalizeEmail } from './email';
import { getAccessGrantByEmail, type AccessGrantRow } from './db';
import { decideAccess, asRole, type AccessVerdict, type Role } from './access';
import type { Grant } from './permissions';
import { setReadOnly } from './readOnly';

// Re-exported so existing consumers keep importing `Role` from '../../lib/auth';
// the canonical definition now lives with the access-precedence core.
export type { Role } from './access';
// The capability-bearing grant (role + division + canAllocate + email) the
// permission predicates consume; surfaced on the auth context.
export type { Grant } from './permissions';

/** The access decision for the current user (the gate consumes this). */
export type Access = AccessVerdict['access'];

/** The resolved session: the access verdict plus the capability-bearing grant
 *  (null unless the verdict is `granted`). */
export interface ResolvedAuth {
  verdict: AccessVerdict;
  grant: Grant | null;
}

/**
 * Resolve the access verdict for an authenticated user — the I/O half of the
 * boundary. It reads the two inputs (ID-token custom claim, access grant) and
 * defers the precedence to the PURE {@link decideAccess} (src/lib/access.ts,
 * exhaustively unit-tested). Verdict semantics:
 *   - `granted` + role: admitted at that role.
 *   - `denied`: a definitive no (no claim, no active grant, unapproved domain,
 *     not on the allowlist) — the gate shows "no access".
 *   - `unknown`: a transient lookup failure with no positive grant; fails OPEN.
 *
 * RESILIENCE: a failed token read or a THROWN grant lookup never denies — a
 * claim still wins (checked first, offline-safe), and an otherwise-ungranted
 * user with a failed lookup resolves to `unknown` (admitted, role unresolved) so
 * a connector hiccup cannot lock the team out. Only a clean, definitive result
 * denies.
 */
export async function resolveAuth(user: User): Promise<ResolvedAuth> {
  const email = normalizeEmail(user.email);

  // 1. Custom claim short-circuits the ACCESS decision — it works with the
  //    connector offline, so a claim-bearing admin can never be locked out by a
  //    Data Connect hiccup. Only admins carry the claim (see syncRoleClaim in
  //    admin-api); division/canAllocate are irrelevant to them because admins
  //    see every division and allocate unconditionally.
  //
  //    ⚠️ BUT AI ELIGIBILITY LIVES ONLY ON THE GRANT ROW, so the row still has
  //    to be read. Returning here without it made `aiEnabled` permanently false
  //    for every administrator — the AI Mode switch in Settings rendered
  //    disabled for exactly the people most likely to be switching it on, while
  //    working correctly for a team lead (who carries no claim and so fell
  //    through to the lookup below).
  //
  //    The lookup is best-effort and deliberately kept OUTSIDE the access
  //    decision: the claim has already granted access, so a throw here leaves
  //    aiEnabled false (fails closed, consistent with the rest of the gate)
  //    without ever denying entry.
  try {
    const token = await user.getIdTokenResult();
    const claimRole = asRole(typeof token.claims.role === 'string' ? token.claims.role : null);
    if (claimRole) {
      let claimRow: AccessGrantRow | null = null;
      try {
        if (email !== '') claimRow = await getAccessGrantByEmail(email);
      } catch {
        /* transient — access is already granted by the claim; AI stays off */
      }
      return {
        verdict: { access: 'granted', role: claimRole },
        grant: {
          role: claimRole,
          division: null,
          canAllocate: false,
          // An admin claim still never CONFERS AI on its own — this reads the
          // explicit per-person grant, so the rollout stays deliberate.
          aiEnabled: claimRow?.aiEnabled ?? false,
          email,
        },
      };
    }
  } catch {
    // A failed token read must not deny — fall through to the grant lookup.
  }

  // 2. Access-grant lookup. A THROW is transient and must never deny; a clean
  //    empty result is a definitive "no grant". decideAccess applies the rest of
  //    the precedence (inactive→deny, allowlist→admin, approved domain→reviewer).
  let row: AccessGrantRow | null = null;
  let grantLookupFailed = false;
  if (email !== '') {
    try {
      row = await getAccessGrantByEmail(email);
    } catch {
      grantLookupFailed = true;
    }
  }
  const verdict = decideAccess({ claimRole: null, grant: row, grantLookupFailed, email });
  if (verdict.access !== 'granted') return { verdict, grant: null };
  // Granted: carry the grant's capability facts. division/canAllocate come from
  // the row when present (teamLead/reviewer/manager); allowlist / approved-domain
  // admits with no row, so they default to none.
  return {
    verdict,
    grant: {
      role: verdict.role,
      division: row?.division ?? null,
      canAllocate: row?.canAllocate ?? false,
      crossDivision: row?.crossDivision ?? false,
      // Fails CLOSED: no row, or a row predating the column, means no AI.
      aiEnabled: row?.aiEnabled ?? false,
      email,
    },
  };
}

/** Back-compat: resolve only the access verdict (the I/O wrapper's original
 *  contract). Delegates to {@link resolveAuth}. */
export async function resolveRole(user: User): Promise<AccessVerdict> {
  return (await resolveAuth(user)).verdict;
}

/** Map common Firebase auth error codes to clear British-English messages. */
function friendlyAuthError(error: unknown): string {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/invalid-email':
    case 'auth/user-not-found':
      return 'Incorrect email address or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact your administrator.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in pop-up. Please allow pop-ups and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/weak-password':
      return 'The new password is too weak. Please choose a stronger one.';
    case 'auth/requires-recent-login':
      return 'Please sign in again before changing your password.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/** An error carrying a UI-ready, British-English message. */
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export interface AuthState {
  user: User | null;
  role: Role | null;
  /** The capability-bearing grant for the current user; null when signed out /
   *  denied / still resolving. Permission predicates (src/lib/permissions) read it. */
  grant: Grant | null;
  /** The access verdict for the current user; null when signed out. */
  access: Access | null;
  loading: boolean;
}

export interface AuthContextValue extends AuthState {
  /** The user's REAL resolved role/grant, ignoring any active impersonation. The
   *  impersonation control + data hooks read these (an admin always reads the full
   *  register; only the VIEW is scoped to the impersonated grant). */
  realRole: Role | null;
  realGrant: Grant | null;
  /** True while an admin is previewing as another role. */
  isImpersonating: boolean;
  /** Begin previewing as the given grant (admins only; no-op otherwise). */
  impersonate: (grant: Grant) => void;
  /** Stop previewing and return to the admin's own view. */
  stopImpersonating: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
  /** Set a new password on an account that already has a password credential.
   *  No reauthentication is required; Firebase may still demand a recent login
   *  if the session is stale (surfaced as "Please sign in again …"). */
  changePassword: (newPassword: string) => Promise<void>;
  /** Set a password on an account that has none (e.g. a Google-only sign-in) by
   *  linking an email/password credential. Reauthenticates with Google if the SDK
   *  requires a recent login. Use changePassword once a password already exists. */
  addPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<AuthState>({
    user: null,
    role: null,
    grant: null,
    access: null,
    loading: true,
  });
  // Admin impersonation ("view as another role"). Held separately from the real
  // resolved state so it can be layered over the top without touching the user's
  // actual identity/token (the admin's reads stay privileged; only the VIEW is
  // scoped). Cleared whenever the real user is no longer an admin (e.g. sign-out).
  const [impersonatedGrant, setImpersonatedGrant] = useState<Grant | null>(null);

  useEffect(() => {
    // `onAuthStateChanged` fires once on mount with the restored session (or
    // null), then on every sign-in / sign-out. Verdict resolution is async, so
    // two rapid events can race across that gap (e.g. sign-out fires null, then a
    // slow resolveRole from the previous user resolves and overwrites it). Track
    // the current user identity and only commit a verdict once the user is still
    // current. Firebase hands back a stable `User` reference per session, so a
    // `===` identity check is reliable.
    let current: User | null = null;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      current = user;
      if (!user) {
        setState({ user: null, role: null, grant: null, access: null, loading: false });
        return;
      }
      // Enter the resolving window: surface the new user immediately but keep
      // `loading` true so the gate / role guards wait for the verdict rather than
      // briefly seeing a stale state and flashing the wrong screen.
      setState((s) => ({ ...s, user, loading: true }));
      void resolveAuth(user).then(({ verdict, grant }) => {
        if (current !== user) return;
        setState({
          user,
          role: verdict.access === 'granted' ? verdict.role : null,
          grant,
          access: verdict.access,
          loading: false,
        });
      });
    });
    return () => {
      current = null;
      unsubscribe();
    };
  }, []);

  // The actions are pure — they read `auth` / `auth.currentUser`, never `state`
  // — so they can be stable across renders. Stability matters: consumers put
  // `signOutUser` (etc.) in effect dependency arrays.
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, normalizeEmail(email), password);
      // State updates via onAuthStateChanged — no manual setState here.
    } catch (error) {
      throw new AuthError(friendlyAuthError(error));
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // State updates via onAuthStateChanged. Whether this Google account is
      // actually admitted is decided by resolveRole (approved domain / explicit
      // grant); an ungranted account lands on the gate's "no access" screen.
    } catch (error) {
      throw new AuthError(friendlyAuthError(error));
    }
  }, []);

  const signOutUser = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw new AuthError(friendlyAuthError(error));
    }
  }, []);

  const changePassword = useCallback(async (newPassword: string) => {
    const current = auth.currentUser;
    if (!current || !current.email) {
      throw new AuthError('You must be signed in to change your password.');
    }
    // Set the new password directly — no current-password reauthentication. If
    // the session is too old Firebase throws requires-recent-login, which maps
    // to "Please sign in again before changing your password."
    try {
      await updatePassword(current, newPassword);
    } catch (error) {
      throw new AuthError(friendlyAuthError(error));
    }
  }, []);

  const addPassword = useCallback(async (newPassword: string) => {
    const current = auth.currentUser;
    if (!current || !current.email) {
      throw new AuthError('You must be signed in to set a password.');
    }
    const credential = EmailAuthProvider.credential(current.email, newPassword);
    const codeOf = (error: unknown): string =>
      typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code: unknown }).code)
        : '';
    // Map a link failure to UI copy. A password (or this credential) already
    // existing — including a race where another tab linked first — points the
    // user at the right tool; everything else uses the shared mapping.
    const linkError = (error: unknown): AuthError =>
      ['auth/provider-already-linked', 'auth/email-already-in-use', 'auth/credential-already-in-use'].includes(
        codeOf(error),
      )
        ? new AuthError('This account already has a password. Use “Change password” instead.')
        : new AuthError(friendlyAuthError(error));
    try {
      await linkWithCredential(current, credential);
    } catch (error) {
      // Linking a credential can require a fresh login. Reauthenticate with the
      // existing Google provider (this flow only fires for Google-only accounts),
      // then retry the link once.
      if (codeOf(error) === 'auth/requires-recent-login') {
        try {
          await reauthenticateWithPopup(current, new GoogleAuthProvider());
          await linkWithCredential(current, credential);
        } catch (reauthError) {
          throw linkError(reauthError);
        }
        return;
      }
      throw linkError(error);
    }
  }, []);

  // Impersonation actions are simple setters; the admin-only gate lives in the
  // `isImpersonating` derivation below, so a non-admin can never get an effective
  // role other than their own even if these were called.
  const impersonate = useCallback((grant: Grant) => setImpersonatedGrant(grant), []);
  const stopImpersonating = useCallback(() => setImpersonatedGrant(null), []);

  // Effective vs real: only an admin may impersonate; impersonation only ever
  // NARROWS the view (it can never grant more than the admin already has).
  const realRole = state.role;
  const realGrant = state.grant;
  const isImpersonating = realRole === 'admin' && impersonatedGrant != null;
  const effectiveGrant: Grant | null = isImpersonating ? impersonatedGrant : state.grant;
  const effectiveRole: Role | null =
    isImpersonating && impersonatedGrant ? impersonatedGrant.role : state.role;

  // While previewing, block user-data writes (src/lib/readOnly) so the preview has
  // no side effects and never mis-attributes the audit trail.
  useEffect(() => {
    setReadOnly(isImpersonating);
    return () => setReadOnly(false);
  }, [isImpersonating]);

  // A preview can never outlive admin rights: drop it if the real user is no
  // longer an admin (sign-out / role change).
  useEffect(() => {
    if (realRole !== 'admin' && impersonatedGrant != null) setImpersonatedGrant(null);
  }, [realRole, impersonatedGrant]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      role: effectiveRole,
      grant: effectiveGrant,
      realRole,
      realGrant,
      isImpersonating,
      impersonate,
      stopImpersonating,
      signIn,
      signInWithGoogle,
      signOutUser,
      changePassword,
      addPassword,
    }),
    [
      state,
      effectiveRole,
      effectiveGrant,
      realRole,
      realGrant,
      isImpersonating,
      impersonate,
      stopImpersonating,
      signIn,
      signInWithGoogle,
      signOutUser,
      changePassword,
      addPassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context. Throws if used outside an <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return ctx;
}
